import { useNavigate } from 'react-router-dom';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { TileIcon, TileLabel, homeTileButtonClass } from '@features/dashboard/TileIcon';
import { runTileAction } from '@shared/lib/tileActions';
import type { CompiledMoreTileCategory, CompiledPublicTile } from '@shared/types/domainConfig';

interface MoreCategoryTilesSheetProps {
  category: CompiledMoreTileCategory | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Swipe-up sheet listing tiles from a `more_categories` group — same role as
 * the member portal home category modal.
 */
export function MoreCategoryTilesSheet({
  category,
  open,
  onOpenChange,
}: MoreCategoryTilesSheetProps): JSX.Element | null {
  const navigate = useNavigate();
  if (!category) return null;

  const tiles = [...(category.tiles ?? [])].sort((a, b) => a.order - b.order);

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={category.name}
      description={category.description ?? undefined}
      height="min(72dvh, 560px)"
    >
      {tiles.length === 0 ? (
        <p className="py-8 text-center text-body-sm text-ink-secondary">
          No links in this category yet.
        </p>
      ) : (
        <ul className="grid grid-cols-4 gap-3 pb-2">
          {tiles.map((tile) => (
            <li key={tile.id}>
              <CategoryTile
                tile={tile}
                onSelect={() => {
                  runTileAction(tile, navigate);
                  onOpenChange(false);
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </BottomSheet>
  );
}

function CategoryTile({
  tile,
  onSelect,
}: {
  tile: CompiledPublicTile;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button type="button" onClick={onSelect} className={homeTileButtonClass}>
      <TileIcon imageUrl={tile.image_url} label={tile.label} />
      <TileLabel label={tile.label} />
    </button>
  );
}
